import React, { useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import { Spinner, Row, Col, Form, InputGroup } from "react-bootstrap";
import ExpandedOrder from "./ExpandedOrder";
import api from "common/api";
import { toast } from "react-toastify";
import { IOrder, OrderInfo } from "common/types";

const AdminOrdersList: React.FC = () => {
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [filteredOrders, setFilteredOrders] = useState<IOrder[]>([]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<IOrder[]>("/orders/");
      const preparationOrders = response.data
        .filter((order) => order.info === OrderInfo.PREPARATIONS)
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
      setOrders(preparationOrders);
      setFilteredOrders(preparationOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Greška prilikom dohvaćanja narudžbi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (search.trim() === "") {
      setFilteredOrders(orders);
    } else {
      const lowerSearch = search.toLowerCase();
      setFilteredOrders(
        orders.filter(
          (order) =>
            order.customer.first_name.toLowerCase().includes(lowerSearch) ||
            order.customer.last_name.toLowerCase().includes(lowerSearch) ||
            order.customer.profile.school.toLowerCase().includes(lowerSearch) ||
            order.course.title.toLowerCase().includes(lowerSearch) ||
            order.id.toLowerCase().includes(lowerSearch),
        ),
      );
    }
  }, [search, orders]);

  const columns = [
    {
      id: "created_at",
      name: "Datum kreiranja",
      selector: (row: IOrder) => row.created_at,
      sortable: true,
      omit: true,
      sortFunction: (rowA: IOrder, rowB: IOrder) =>
        new Date(rowA.created_at).getTime() -
        new Date(rowB.created_at).getTime(),
    },
    {
      name: "Ime",
      selector: (row: IOrder) => row.customer.first_name,
      sortable: true,
    },
    {
      name: "Prezime",
      selector: (row: IOrder) => row.customer.last_name,
      sortable: true,
    },
    {
      name: "Plaćeno",
      selector: (row: IOrder) => (row.paid ? "Da" : "Ne"),
      cell: (row: IOrder) => (
        <div
          style={{
            backgroundColor: row.paid ? "#d4edda" : "#f8d7da",
            color: row.paid ? "#155724" : "#721c24",
            padding: "5px 10px",
            borderRadius: "4px",
            display: "inline-block",
          }}
        >
          {row.paid ? "Da" : "Ne"}
        </div>
      ),
      sortable: true,
    },
    {
      name: "Grupa priprema",
      selector: (row: IOrder) => row.course.title,
      sortable: true,
    },
    {
      name: "Iznos",
      selector: (row: IOrder) => `${row.amount} EUR`,
      sortable: true,
    },
  ];

  // Handle row expansion
  const ExpandedComponent = ({ data }: { data: IOrder }) => {
    return <ExpandedOrder data={data} />;
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-4">Narudžbe državne mature</h4>
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Pretraži narudžbe..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <InputGroup.Text
                style={{ cursor: "pointer" }}
                onClick={() => setSearch("")}
                title="Obriši pretragu"
              >
                &#10005; {/* Unicode for 'X' mark */}
              </InputGroup.Text>
            )}
          </InputGroup>
        </Col>
      </Row>
      <DataTable
        columns={columns}
        data={filteredOrders}
        defaultSortFieldId="created_at"
        defaultSortAsc={false}
        pagination
        highlightOnHover
        expandableRows
        expandableRowsComponent={ExpandedComponent}
        noDataComponent="Ne postoje narudžbe"
      />
    </div>
  );
};

export default AdminOrdersList;
